import { useState } from "react";
import { Link, useParams } from "react-router";
import { useT } from "../../../lib/use-t";
import { WordList } from "../../../components/woorden/WordList";
import { WordDetailPane } from "../../../components/woorden/WordDetailPane";
import { getEntriesByTopic } from "../../../data/vocab/library";
import { TOPIC_DISPLAY } from "../../../data/vocab/topic-display";
import { isTopicKey } from "../../../data/vocab/types";

function BackToTopics({ label }: { label: string }) {
  return (
    <Link
      to="/woorden"
      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-strong)] bg-[var(--panel)] pl-2.5 pr-3.5 py-2 text-sm font-semibold text-[var(--ink-muted)] transition-colors hover:bg-[var(--panel-soft)] hover:text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M15 6l-6 6 6 6" />
      </svg>
      {label}
    </Link>
  );
}

export function WoordenTopicPage() {
  const t = useT();
  const { topic: raw } = useParams<{ topic: string }>();

  if (!raw || !isTopicKey(raw)) {
    return (
      <div className="mx-auto w-full max-w-[1100px] px-5 sm:px-6 py-6 sm:py-8 space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("woorden.empty.topicNotFound")}
        </h1>
        <BackToTopics label={t("woorden.backToTopics")} />
      </div>
    );
  }

  const entries = getEntriesByTopic(raw);
  const display = TOPIC_DISPLAY[raw];

  if (entries.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[1100px] px-5 sm:px-6 py-6 sm:py-8 space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">{display.title}</h1>
        <p className="font-serif-italic text-[var(--ink-muted)]">{t("woorden.empty.noWords")}</p>
        <BackToTopics label={t("woorden.backToTopics")} />
      </div>
    );
  }

  // key on the topic so selection + mobile view reset when the topic changes
  // (react-router reuses the component across param changes otherwise).
  return <TopicDetailBody key={raw} title={display.title} entries={entries} total={entries.length} />;
}

function TopicDetailBody({
  title,
  entries,
  total,
}: {
  title: string;
  entries: ReturnType<typeof getEntriesByTopic>;
  total: number;
}) {
  const t = useT();
  const [selectedId, setSelectedId] = useState(entries[0]?.id);
  // 'list' | 'detail' drives the single-column mobile view switch. Desktop
  // (lg+) shows both panes regardless of this value.
  const [mobileView, setMobileView] = useState<"list" | "detail">("list");
  const selected = entries.find((e) => e.id === selectedId) ?? entries[0];

  function pick(id: string) {
    setSelectedId(id);
    setMobileView("detail");
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] px-5 sm:px-6 py-6 sm:py-8">
      {/* Detail head — hidden on mobile when the detail pane is open (it owns
          its own back bar). */}
      <div className={`mb-5 flex items-center gap-3.5 ${mobileView === "detail" ? "hidden lg:flex" : "flex"}`}>
        <BackToTopics label={t("woorden.backToTopics")} />
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-[26px] font-semibold tracking-tight truncate">
            {title}
          </h1>
          <span className="font-mono-badge text-xs text-[var(--ink-faint)] tabular-nums">
            {t("woorden.topicTotalFmt", { count: total })}
          </span>
        </div>
      </div>

      <div className="grid gap-[22px] items-start lg:grid-cols-[1fr_1.15fr]">
        {/* List pane */}
        <div
          className={`rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-2.5 shadow-[var(--shadow)] max-h-[560px] overflow-auto ${
            mobileView === "detail" ? "hidden lg:block" : "block"
          }`}
        >
          <WordList entries={entries} selectedId={selectedId} onSelect={pick} />
        </div>

        {/* Detail pane */}
        <div
          className={`rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] shadow-[var(--shadow)] ${
            mobileView === "list" ? "hidden lg:block" : "block"
          }`}
        >
          {/* Mobile-only back-to-list bar */}
          <div className="lg:hidden border-b border-[var(--border)] px-3 py-2.5">
            <button
              type="button"
              onClick={() => setMobileView("list")}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-strong)] bg-[var(--panel)] pl-2.5 pr-3.5 py-1.5 text-sm font-semibold text-[var(--ink-muted)] transition-colors hover:bg-[var(--panel-soft)] hover:text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M15 6l-6 6 6 6" />
              </svg>
              {t("woorden.backToList")}
            </button>
          </div>
          <div className="p-5 sm:p-7">
            {selected ? <WordDetailPane entry={selected} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
